import { FC, useMemo } from "react";
import { useViewList, useViewListContext } from "@/context/ViewListContext";
import ViewList, { ViewListItem } from "@/components/layout/ViewList";
import { AlternatingMessages, Loader, useCourseEditorContext, useGoBack } from "@vixi/ui";
import { Controller, UseFormReturn, useForm } from "react-hook-form";
import { useTranslation } from "@vixi/localization";
import { SelectCharacterPreferences } from "./CharacterPreferences/SelectCharacterPreferences";
import {
  GenerateAvatarRequest,
  useGenerateCourseAvatar,
} from "@/hooks/api/avatars/useGenerateCourseAvatar";
import { PreferenceFormValues } from "./CharacterPreferences/PreferenceForm";
import { ReviewCharacterStep } from "./ReviewCharacterStep";
import { Button, Img, FileUploadBox } from "@vixi/ui";

// Assets
import HeadshotBubbleEn from "@assets/bubbles/headshot.png";
import HeadshotBubbleAr from "@assets/bubbles/ar-headshot.png";

// Constants
const HeadshotBubble = { ar: HeadshotBubbleAr, en: HeadshotBubbleEn } as const;

// Types
type CreateAvatarData = Omit<GenerateAvatarRequest, "purpose">;

interface GenerateCharacterFormValues extends PreferenceFormValues {
  image?: File[];
}

interface CreateCharacterStepProps {
  onCompleted?: () => void;
}

export const CreateCharacterStep: FC<CreateCharacterStepProps> = ({ ...props }) => {
  const { t, byLocale, tFile } = useTranslation();

  const mainFlow = useViewListContext();
  const viewList = useViewList({
    onCompleted: () => {
      mainFlow?.nextStep();
      if (props.onCompleted) props.onCompleted();
    },
  });

  // Go back logic
  const goBack = useMemo(
    () => (viewList.activeStep === 0 ? (mainFlow?.prevStep ?? null) : null),
    [viewList.activeStep],
  );
  useGoBack(goBack, {
    allowChanges: true,
  });

  // Store
  const { setValue } = useCourseEditorContext();

  // Upload logic
  const preferencesForm = useForm<GenerateCharacterFormValues>({
    defaultValues: {
      image: [],
    },
  });
  const { control, watch, getValues } = preferencesForm;

  const file = watch("image")?.[0];

  const {
    mutateAsync: generateAvatar,
    isError: imageUploadFailed,
    isSuccess: imageUploaded,
    data: generatedAvatars,
  } = useGenerateCourseAvatar();

  const onGenerateAvatar = async () => {
    if (!file) return;

    if (viewList.activeStep === 2) viewList.nextStep();

    const { image, outfit, ...values } = getValues();
    const avatars = await generateAvatar({ ...values, clothes: outfit, image: file });

    setValue(["courseCharacters"], avatars, { mustExist: false });
  };

  return (
    <ViewList {...viewList}>
      {/* 0. Let’s generate an avatar! */}
      <ViewListItem>
        <div className="m-auto flex">
          <Img contain src="/images/bubble-lookalike.png" className="max-h-90 -mt-20 min-w-0" />
          <Img
            contain
            src="/images/screens/lookalike-in-course-1.png"
            className="max-h-67.25 min-w-0"
          />
          <Img
            contain
            src="/images/screens/lookalike-in-course-2.png"
            className="-ms-10.25 mt-0.5 max-h-67.25 min-w-0"
          />
        </div>

        <div className="fixed end-18.75 bottom-13">
          <Button
            onClick={() => viewList.nextStep()}
            size="large"
            variant="contained"
            className="fs-20"
          >
            {t("CreateAvatar.LetsGenerateAnAvatar")}
          </Button>
        </div>
      </ViewListItem>

      {/* 1. Upload Image */}
      <ViewListItem>
        <div className="m-auto">
          <Controller
            control={control}
            name="image"
            render={({ field }) => (
              <FileUploadBox
                accept="image/jpeg, image/png, image/webp"
                image={{
                  src: byLocale({
                    ...HeadshotBubble,
                    fallback: tFile("/images/bubbles/", "headshot.png"),
                  }),
                  alt: "Fox Icon",
                }}
                imageSize={{ width: "509px", height: "509px" }}
                dropzoneLabel={t("Action.DropPicture")}
                imageMarginBlock={byLocale({ ar: "-160px 0", fallback: "-170px 0" })}
                {...field}
              />
            )}
          />
        </div>

        {file && (
          <div className="absolute end-20 bottom-12.5">
            <Button variant="contained" size="large" type="submit" onClick={viewList.nextStep}>
              {t("Action.Next")}
            </Button>
          </div>
        )}
      </ViewListItem>

      {/* 2. Character preferences */}
      <ViewListItem>
        <SelectCharacterPreferences
          preferencesForm={preferencesForm as UseFormReturn<PreferenceFormValues>}
          onCompleted={onGenerateAvatar}
        />
      </ViewListItem>

      {/* 3. Loading (Generating Image) */}
      <ViewListItem>
        <div className="m-auto">
          <Loader
            isFailed={imageUploadFailed}
            onRetry={() => {
              onGenerateAvatar();
            }}
            loadingText="CreateAvatar.GeneratingAvatar"
            completionText="CreateAvatar.GeneratedSuccessfully"
            isCompleted={imageUploaded}
            width={300}
          />
          {!imageUploaded && (
            <p className="mt-2 text-center">
              <AlternatingMessages
                messages={["LoadingMessage.Stretch", "LoadingMessage.GeneratingYourAvatar"]}
              />
            </p>
          )}
        </div>

        {imageUploaded && (
          <Button
            variant="contained"
            size="large"
            type="submit"
            onClick={viewList.nextStep}
            className="absolute end-20 bottom-12.5"
          >
            {t("Action.Next")}
          </Button>
        )}
      </ViewListItem>

      {/* 4. Character Preview */}
      <ViewListItem>
        {generatedAvatars && (
          <ReviewCharacterStep onNext={viewList.nextStep} avatars={generatedAvatars} />
        )}
      </ViewListItem>
    </ViewList>
  );
};
